import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

export default function ManageTeam() {
  const { trainer, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(null); // trainer being assigned to
  const [unassigned, setUnassigned] = useState([]);
  const [composeFor, setComposeFor] = useState(null);
  const [msgForm, setMsgForm] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    api.get('/settings/team').then(setTeam).finally(() => setLoading(false));
  }, [isAdmin, navigate]);

  async function handleRemove(member) {
    if (!window.confirm(`Are you sure you want to remove ${member.name}? Their clients will be returned to you for reassignment.`)) return;
    try {
      await api.delete('/settings/team/' + member.id);
      setTeam(prev => prev.filter(m => m.id !== member.id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function openAssign(member) {
    const clients = await api.get('/settings/team/unassigned-clients').catch(() => []);
    setUnassigned(clients);
    setAssignModal(member);
  }

  async function handleAssign(clientId) {
    try {
      await api.put('/settings/clients/' + clientId + '/assign', { trainer_id: assignModal.id });
      setTeam(prev => prev.map(m => m.id === assignModal.id ? { ...m, client_count: parseInt(m.client_count) + 1 } : m));
      setUnassigned(prev => prev.filter(c => c.id !== clientId));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleSendMessage(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/messages', { recipient_id: composeFor.id, subject: msgForm.subject, body: msgForm.body });
      setComposeFor(null);
      setMsgForm({ subject: '', body: '' });
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <h1>
          <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
            <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
          </button>
          Manage Team
        </h1>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {team.map(member => (
            <div key={member.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{member.name}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>{member.email}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 8px', borderRadius: 6, background: member.role === 'admin' ? 'rgba(215,38,56,0.12)' : 'rgba(46,204,113,0.1)', color: member.role === 'admin' ? 'var(--color-primary)' : 'var(--color-success)' }}>
                    {member.role}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{member.client_count} client{member.client_count !== '1' ? 's' : ''}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setComposeFor(member); setMsgForm({ subject: '', body: '' }); }}
                  style={{ flex: 1, minHeight: 36, background: 'rgba(215,38,56,0.08)', border: '1px solid rgba(215,38,56,0.2)', color: 'var(--color-primary)', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Message
                </button>
                <button onClick={() => openAssign(member)}
                  style={{ flex: 1, minHeight: 36, background: 'rgba(46,204,113,0.08)', border: '1px solid rgba(46,204,113,0.2)', color: 'var(--color-success)', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Assign Client
                </button>
                {member.id !== trainer?.id && (
                  <button onClick={() => handleRemove(member)}
                    style={{ minHeight: 36, minWidth: 36, background: 'rgba(215,38,56,0.05)', border: '1px solid rgba(215,38,56,0.15)', color: 'var(--color-primary)', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 18, cursor: 'pointer' }}>
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button onClick={() => navigate('/settings/invite')} className="btn btn-primary" style={{ marginTop: 24 }}>
          + Invite Team Member
        </button>
      </div>

      {/* Assign Client Modal */}
      {assignModal && (
        <div onClick={() => setAssignModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', background: 'var(--color-surface)', borderRadius: '16px 16px 0 0', padding: 20, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Assign client to {assignModal.name}</div>
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {unassigned.length === 0 && <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', marginTop: 16 }}>No unassigned clients</p>}
              {unassigned.map(c => (
                <button key={c.id} onClick={() => handleAssign(c.id)}
                  style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', background: 'var(--color-bg)', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', textAlign: 'left' }}>
                  {c.first_name} {c.last_name}
                </button>
              ))}
            </div>
            <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => setAssignModal(null)}>Done</button>
          </div>
        </div>
      )}

      {/* Compose Message Modal */}
      {composeFor && (
        <div onClick={() => setComposeFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', background: 'var(--color-surface)', borderRadius: '16px 16px 0 0', padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Message {composeFor.name}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>{composeFor.email}</div>
            <form onSubmit={handleSendMessage}>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input className="form-input" placeholder="Optional subject" value={msgForm.subject} onChange={e => setMsgForm(f => ({ ...f, subject: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-textarea" placeholder="Type your message..." value={msgForm.body} onChange={e => setMsgForm(f => ({ ...f, body: e.target.value }))} required style={{ minHeight: 100 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={sending} style={{ flex: 1 }}>{sending ? 'Sending...' : 'Send'}</button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setComposeFor(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
